import { useState } from 'react';
import { useToast } from '@repo/design-system-kit/components/toast/toast';
import { useIntl } from 'react-intl';
import { MFA_STATUSES, MfaModal, type MfaStatus } from '#components/flows/mfa-modal';
import { type MfaMethod, useUser } from '../providers/user-provider';
import { useActivateSms, useOtpSms, useTriggerSms } from '../api/mfa-otp';
import type { ErrorComponentProps } from './sensitive-action';
import { getInitialMfaStatus } from './get-initial-mfa-status';
import { getMFAError, INVALID_MFA, isScaError } from '../utils/sca-errors';

export function MfaErrorUI({
  onClose,
  onSensitiveAction,
  errorDetails,
}: ErrorComponentProps): JSX.Element {
  const { showToast } = useToast();
  const user = useUser();
  const { formatMessage } = useIntl();
  const [mfaStatus, setMfaStatus] = useState<MfaStatus>(() =>
    getInitialMfaStatus(errorDetails, user)
  );
  const [otpErrorMessage, setOtpErrorMessage] = useState<string | undefined>(undefined);

  // oxlint-disable-next-line typescript/consistent-type-assertions
  const mfaMethod = (errorDetails.mfaMethod || user.mfaMethod || 'by_sms') as MfaMethod;

  const { mutateAsync: sendOtp } = useOtpSms();
  const { mutateAsync: triggerSms } = useTriggerSms();
  const { mutateAsync: activateSms } = useActivateSms();

  const handleGetStarted = (): void => {
    setMfaStatus(MFA_STATUSES.VERIFY_NUMBER);
  };

  const handleVerifyNumber = async (): Promise<void> => {
    try {
      await sendOtp();
      setMfaStatus(MFA_STATUSES.CONFIRMATION);
    } catch {
      setMfaStatus(MFA_STATUSES.FAILURE);
    }
  };

  const handleSendSMS = async (): Promise<void> => {
    try {
      await triggerSms(undefined, {
        onSuccess: () => {
          showToast({
            type: 'success',
            text: formatMessage({ id: 'otp.toast-success' }),
          });
        },
      });
    } catch {
      setMfaStatus(MFA_STATUSES.FAILURE);
    }
  };

  const handleConfirmOtp = async ({ otpToken }: { otpToken: string }): Promise<void> => {
    setOtpErrorMessage(undefined);
    try {
      // NOTE: if the mfa method is not defined we need to activate the sms method
      if (!errorDetails.mfaMethod || errorDetails.mfaMethod === '') {
        await activateSms({ otpToken });
      }
      await onSensitiveAction({
        header: 'X-Qonto-MFA',
        token: otpToken,
      });
    } catch (error) {
      if (isScaError(error)) {
        const mfaError = getMFAError(error);
        if (mfaError?.code === INVALID_MFA) {
          setOtpErrorMessage(formatMessage({ id: 'otp.invalid_otp_token' }));
        } else {
          setMfaStatus(MFA_STATUSES.FAILURE);
        }
      } else {
        setMfaStatus(MFA_STATUSES.FAILURE);
      }
    }
  };

  return (
    <MfaModal
      method={mfaMethod}
      onClose={onClose}
      onConfirmOtp={handleConfirmOtp}
      onGetStarted={handleGetStarted}
      onSendSMS={handleSendSMS}
      onVerifyNumber={handleVerifyNumber}
      otpErrorMessage={otpErrorMessage}
      status={mfaStatus}
    />
  );
}
